import React, { useMemo } from 'react';

import {
  Button,
  Divider,
  Grid,
  Group,
  LoadingOverlay,
  NumberInput,
  Paper,
  Select,
  Text,
  Title,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import dayjs from 'dayjs';
import useUpdateGym from 'hooks/gym/useUpdateGym';
import { TimePeriod, TimeUnitEnum } from 'types/enums';
import { GymRetrieve } from 'types/generated';
import { getIntegerValueAndTimeUnitFromMinutes, getTimeUnitInMinutes } from 'utils/date-functions';
import notificationHandler from 'utils/notifications';

interface FormValues {
  missingAthleteThreshold: number;
  missingAthleteThresholdUnit: TimePeriod;
  packageAutoRenewThreshold: number;
  packageAutoRenewThresholdUnit: TimePeriod;
  businessMetricsStartDate: Date;
}

const timeUnitOptions = [
  { value: TimeUnitEnum.MINUTE, label: `Min(s)` },
  { value: TimeUnitEnum.HOUR, label: `Hour(s)` },
  { value: TimeUnitEnum.DAY, label: `Day(s)` },
];

const thisMonth = new Date();
thisMonth.setTime(Date.now());

const NotificationsCard = ({
  gymData,
  gymDataIsLoading,
  gymDataIsSuccess,
  permission = true,
}: {
  gymData?: GymRetrieve;
  gymDataIsLoading: boolean;
  gymDataIsSuccess: boolean;
  permission?: boolean;
}): JSX.Element => {
  const initialValues = useMemo((): FormValues => {
    const additionalSettings = gymData?.additionalSettings;

    const { value: missingAthleteThreshold, timeUnit: missingAthleteThresholdUnit } =
      getIntegerValueAndTimeUnitFromMinutes(additionalSettings?.missingAthleteThreshold || 0);
    const { value: packageAutoRenewThreshold, timeUnit: packageAutoRenewThresholdUnit } =
      getIntegerValueAndTimeUnitFromMinutes(additionalSettings?.packageAutoRenewThreshold || 0);

    return {
      missingAthleteThreshold,
      missingAthleteThresholdUnit,
      packageAutoRenewThreshold,
      packageAutoRenewThresholdUnit,
      businessMetricsStartDate: additionalSettings?.businessMetricsStartDate
        ? new Date(additionalSettings?.businessMetricsStartDate)
        : gymData?.createdAt
        ? new Date(gymData?.createdAt)
        : new Date(),
    };
  }, [gymData?.additionalSettings, gymData?.createdAt]);

  const updateMutation = useUpdateGym();

  const [isEditable, { open, close }] = useDisclosure(false);

  const form = useForm<FormValues>({
    initialValues,
  });

  const handleSubmit = (formData: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    const missingAthleteThresholdMinutes = getTimeUnitInMinutes(
      formData.missingAthleteThreshold,
      formData.missingAthleteThresholdUnit,
    );
    const packageAutoRenewThresholdMinutes = getTimeUnitInMinutes(
      formData.packageAutoRenewThreshold,
      formData.packageAutoRenewThresholdUnit,
    );

    if (gymData) {
      const request = {
        ...gymData,
        gymMailConfig: null,
        additionalSettings: {
          missingAthleteThreshold: missingAthleteThresholdMinutes,
          packageAutoRenewThreshold: packageAutoRenewThresholdMinutes,
          businessMetricsStartDate: dayjs(formData.businessMetricsStartDate).format('YYYY-MM'),
          requireAthletePhoneNumberVerificationPerChange:
            gymData?.additionalSettings?.requireAthletePhoneNumberVerificationPerChange || false,
        },
      };

      updateMutation.mutate(request, {
        onSuccess: response => {
          successNotification('Gym settings update success', `${response.data.title} successfully updated.`);
          close();
        },
        onError: error => {
          errorNotification('Gym settings update failed', error.response?.data.message);
        },
      });
    }
  };

  const handleCancelChanges = (): void => {
    form.setValues(initialValues);
    close();
  };

  useShallowEffect(() => {
    if (gymData && gymDataIsSuccess) {
      form.setValues(initialValues);
    }
  }, [gymData?.additionalSettings]);

  return (
    <Paper shadow='xl' p='sm'>
      <LoadingOverlay visible={gymDataIsLoading} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Group position='apart'>
          <Title order={4}>Additional Settings</Title>

          <Group spacing='xs'>
            {isEditable ? (
              <>
                <Button color='red' compact onClick={handleCancelChanges} loading={updateMutation.isLoading}>
                  Cancel
                </Button>
                <Button
                  type='submit'
                  color='green'
                  compact
                  loading={updateMutation.isLoading}
                  data-cy='gymSettingsAdditionalSettingsSaveButton'
                >
                  Save
                </Button>
              </>
            ) : (
              <Button
                compact
                onClick={open}
                disabled={!permission}
                loading={gymDataIsLoading || updateMutation.isLoading}
                data-cy='gymSettingsAdditionalSettingsEditButton'
              >
                Edit
              </Button>
            )}
          </Group>
        </Group>

        <Divider mx='-sm' my='sm' />

        <Grid align='center' gutter={6} p='xs'>
          <Grid.Col xs={12} sm={3}>
            <Group noWrap grow spacing='xs'>
              <NumberInput
                size='xs'
                disabled={!isEditable}
                min={0}
                {...form.getInputProps('missingAthleteThreshold')}
                data-cy='gymSettingsAdditionalSettingsMissingAthlete'
              />
              <Select
                size='xs'
                data={timeUnitOptions}
                disabled={!isEditable}
                {...form.getInputProps('missingAthleteThresholdUnit')}
                data-cy='gymSettingsAdditionalSettingsMissingAthleteSelect'
              />
            </Group>
          </Grid.Col>
          <Grid.Col xs={12} sm={9} pl='sm'>
            <Text size='sm'>The amount of time an athlete is considered missing</Text>
          </Grid.Col>
        </Grid>
        <Grid align='center' gutter={6} p='xs'>
          <Grid.Col xs={12} sm={3}>
            <Group noWrap grow spacing='xs'>
              <NumberInput
                size='xs'
                disabled={!isEditable}
                min={0}
                {...form.getInputProps('packageAutoRenewThreshold')}
                data-cy='gymSettingsAdditionalSettingsPackageAutoRenew'
              />
              <Select
                size='xs'
                data={timeUnitOptions}
                disabled={!isEditable}
                {...form.getInputProps('packageAutoRenewThresholdUnit')}
                data-cy='gymSettingsAdditionalSettingsPackageAutoRenewSelect'
              />
            </Group>
          </Grid.Col>
          <Grid.Col xs={12} sm={9} pl='sm'>
            <Text size='sm'>
              Time-based packages with Auto-renew ON will be automatically renewed ___ days prior to the
              package end date.
            </Text>
          </Grid.Col>
        </Grid>
        <Grid align='center' gutter={6} p='xs'>
          <Grid.Col xs={12} sm={3}>
            <Group noWrap grow spacing='xs'>
              <MonthPickerInput
                valueFormat='MMMM YYYY'
                minDate={new Date(gymData?.createdAt || '')}
                maxDate={thisMonth}
                allowDeselect={false}
                required
                disabled={!isEditable}
                {...form.getInputProps('businessMetricsStartDate')}
              />
            </Group>
          </Grid.Col>
          <Grid.Col xs={12} sm={9} pl='sm'>
            <Text size='sm'>
              Set the start date for your Churn, Average Churn, Estimated LEG, and LTV metrics.
            </Text>
          </Grid.Col>
        </Grid>
      </form>
    </Paper>
  );
};

export default NotificationsCard;
